import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { StoreSummaryDto } from '@bhops/shared';
import { UserRole } from '@bhops/shared';
import { storesApi, type ListStoresQuery } from '@/api/stores';
import { extractApiError } from '@/api/client';
import { Button } from '@/components/Form';
import { useAuthStore } from '@/stores/auth';
import { StoreCreateModal } from './stores/StoreCreateModal';
import { StoreEditModal } from './stores/StoreEditModal';

const PAGE_SIZE = 50;

interface Props {
  /**
   * Plan v0.3.0 §F-3 — when true, the page renders in a tighter
   * shell suitable for embedding inside CustomerMasterPage's
   * sub-tab. Skips the page header (h1 + total counter) and the
   * search bar (the parent already has search context).
   */
  embedded?: boolean;
  /**
   * §F-3 — when set, filter the list to this customer's stores
   * only. Passed through to `storesApi.list({ customerId })`.
   */
  customerId?: string;
}

export function StoresPage({ embedded = false, customerId }: Props = {}) {
  const [filters, setFilters] = useState<ListStoresQuery>({
    page: 1,
    pageSize: PAGE_SIZE,
    ...(customerId ? { customerId } : {}),
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<StoreSummaryDto | null>(null);
  const qc = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role);
  const canCreate = userRole === UserRole.ADMIN || userRole === UserRole.MANAGER;
  const canEdit = canCreate;
  // data-lifecycle §3 — 삭제됨 매장 노출/복구는 삭제 권한(=백엔드 CASL delete Store)
  // 보유자에게만: ADMIN/MANAGER/SALES_MANAGER.
  const canRestore =
    userRole === UserRole.ADMIN ||
    userRole === UserRole.MANAGER ||
    userRole === UserRole.SALES_MANAGER;

  const restore = useMutation({
    mutationFn: (id: string) => storesApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stores'] }),
  });

  // Always force the customerId filter when embedded so the user
  // can't override it via the search box. Outside embedded mode
  // the prop is undefined and the filter operates normally.
  const effectiveFilters: ListStoresQuery = customerId ? { ...filters, customerId } : filters;

  const { data, isLoading, error } = useQuery({
    queryKey: ['stores', effectiveFilters],
    queryFn: () => storesApi.list(effectiveFilters),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: embedded ? 8 : 16 }}>
      {/* §F-3 — page header is hidden in embedded mode; the parent
          (CustomerMasterPage detail header) already shows the
          customer's name + meta. The toolbar still renders so
          search/create remain available. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!embedded && (
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            매장
          </h1>
        )}
        <input
          placeholder="매장이나 지점 이름으로 검색"
          value={filters.search ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value || undefined, page: 1 }))
          }
          style={{
            marginLeft: embedded ? 0 : 12,
            width: 240,
            height: 32,
            padding: '0 10px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-input)',
            background: 'var(--bg-surface)',
            fontSize: 13,
          }}
        />
        {/* data-lifecycle §3 — 삭제됨 매장 포함(삭제 권한자 전용). 복구용. */}
        {canRestore && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              padding: '4px 10px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-btn)',
              background: filters.includeDeleted ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
              color: filters.includeDeleted ? 'var(--fg-brand)' : 'var(--fg-default)',
              cursor: 'pointer',
              fontWeight: filters.includeDeleted ? 600 : 400,
            }}
            title="삭제된 매장도 함께 보여 줍니다. 여기서 복구할 수 있습니다."
          >
            <input
              type="checkbox"
              checked={!!filters.includeDeleted}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  includeDeleted: e.target.checked || undefined,
                  page: 1,
                }))
              }
              style={{ margin: 0 }}
            />
            삭제됨 포함
          </label>
        )}
        <div className="kuun-caption" style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
          총{' '}
          <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
            {total.toLocaleString()}
          </span>{' '}
          곳
        </div>
        {canCreate && (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            + 새 매장
          </Button>
        )}
      </div>

      <StoreCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        // §F-3 follow-up — pre-select the embedded customer so the
        // create modal doesn't ask the operator to re-pick.
        initialCustomerId={customerId}
      />

      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <div style={empty}>
            <span className="kuun-caption">불러오는 중…</span>
          </div>
        ) : error ? (
          <div style={empty}>
            <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</span>
          </div>
        ) : items.length === 0 ? (
          <div style={empty} className="kuun-dot-grid">
            <div className="kuun-h4">등록된 매장이 없습니다</div>
            <div className="kuun-caption" style={{ marginTop: 4 }}>
              우측 상단 "+ 새 매장" 버튼으로 등록할 수 있습니다.
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>매장</th>
                <th style={th}>지점</th>
                {/* PDF §Phase C 페이지 1 — 영업 4개 탭 cross-link.
                    매장 row 에서 고객사 직접 navigation. */}
                <th style={th}>고객사</th>
                <th style={th}>지역</th>
                <th style={th}>국가</th>
                <th style={th}>상태</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  style={{
                    borderTop: '1px solid var(--border-subtle)',
                    // data-lifecycle §3 — 삭제됨 row 는 흐리게.
                    opacity: s.deletedAt ? 0.55 : 1,
                  }}
                >
                  {/* docs/work_log/pdf-feedback-followups-2026-05-24 §StoreDetail —
                      매장명 클릭 → /business/stores/:id 단일 상세 진입점. */}
                  <td style={td}>
                    {/* data-lifecycle §3 — 삭제된 매장은 상세(get)가 404 이므로 비링크. */}
                    {s.deletedAt ? (
                      <span style={{ fontWeight: 500 }}>{s.name}</span>
                    ) : (
                      <Link
                        to={`/business/stores/${s.id}`}
                        style={{
                          color: 'var(--fg-brand)',
                          textDecoration: 'none',
                          fontWeight: 500,
                        }}
                      >
                        {s.name}
                      </Link>
                    )}
                  </td>
                  <td style={td}>{s.branchName ?? '–'}</td>
                  <td style={td}>
                    {s.customer ? (
                      <Link
                        to={`/customers/${s.customer.id}`}
                        style={{ color: 'var(--fg-brand)', textDecoration: 'none' }}
                      >
                        {s.customer.displayName} →
                      </Link>
                    ) : (
                      <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                        미지정
                      </span>
                    )}
                  </td>
                  <td style={td}>{[s.city, s.region].filter(Boolean).join(' · ') || '–'}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{s.country}</td>
                  <td style={td}>
                    {s.deletedAt ? (
                      <span style={{ color: 'var(--severity-urgent)', fontWeight: 600 }}>
                        삭제됨
                      </span>
                    ) : s.active ? (
                      <span style={{ color: 'var(--robot-operating)' }}>활성</span>
                    ) : (
                      <span style={{ color: 'var(--fg-muted)' }}>비활성</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {/* data-lifecycle §3 — 삭제된 매장은 복구만 노출(메뉴/편집 숨김). */}
                      {s.deletedAt ? (
                        canRestore && (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={restore.isPending}
                            onClick={() => restore.mutate(s.id)}
                          >
                            복구
                          </Button>
                        )
                      ) : (
                        <>
                          {/* docs/work_log/pdf-feedback-followups-2026-05-24 §menu-offerings-tab —
                              기존 별도 페이지 대신 StoreDetailPage 의 '메뉴 구성'
                              tab 으로 진입. 기존 URL (/stores/:id/menu-offerings)
                              도 router redirect 로 유지. */}
                          <Link
                            to={`/business/stores/${s.id}`}
                            style={{
                              fontSize: 12,
                              color: 'var(--fg-brand)',
                              textDecoration: 'none',
                              padding: '4px 8px',
                              border: '1px solid var(--border-default)',
                              borderRadius: 'var(--r-input)',
                            }}
                          >
                            메뉴 구성
                          </Link>
                          {canEdit && (
                            <Button type="button" variant="secondary" onClick={() => setEditing(s)}>
                              편집
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editing && (
          <StoreEditModal
            open={Boolean(editing)}
            onClose={() => setEditing(null)}
            store={editing}
          />
        )}
      </div>
    </div>
  );
}

const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = { padding: 40, textAlign: 'center' };
